import { tokenStore } from "../utils/tokenStore";
import { parseJwt } from "./jwtUtils";

export const Perm = {
  Schedule_Manage: "Schedule.Manage",
  User_View: "User.View",
  User_Manage: "User.Manage",
  Doctor_Manage: "Doctor.Manage",
  Report_View: "Report.View",
  Report_Log: "Report.Log",
  Clinic_Manage: "Clinic.Manage",
  Organization_Manage: "Organization.Manage",
  Organization_View: "Organization.View",
  Appointment_Manage: "Appointment.Manage",
  Appointment_View: "Appointment.View",
  Appointment_Request_Manage: "Appointment.Request"
} as const;

export const Roles = {
  Admin: "Admin",
} as const;

export type RoleKey = (typeof Roles)[keyof typeof Roles];

const normalizeStringArray = (raw: unknown): string[] => {
  if (Array.isArray(raw))
    return raw.filter((x): x is string => typeof x === "string");
  if (typeof raw === "string") return [raw];
  return [];
};

export type PermKey = (typeof Perm)[keyof typeof Perm];

const normalizePermissions = (raw: unknown): string[] => {
  if (Array.isArray(raw)) {
    return raw.filter((item): item is string => typeof item === "string");
  }
  if (typeof raw === "string") {
    return [raw];
  }
  return [];
};

export const getRolesFromToken = (token?: string | null): string[] => {
  if (!token) return [];
  const payload = parseJwt(token);

  const raw =
    payload?.role ??
    payload?.roles ??
    payload?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] ??
    payload?.["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role"];

  return normalizeStringArray(raw);
};

export const getRoles = (): string[] => {
  const { token } = tokenStore.get();
  return getRolesFromToken(token);
};

export const hasAnyRole = (required: string[] = [], roles = getRoles()) =>
  required.length === 0 || required.some((r) => roles.includes(r));

export const isAdmin = (roles = getRoles()) => roles.includes(Roles.Admin);

export const getPermissionsFromToken = (token?: string | null): string[] => {
  if (!token) return [];
  const payload = parseJwt(token);
  return normalizePermissions(payload?.permission ?? payload?.permissions);
};

export const getPermissionDeniesFromToken = (
  token?: string | null
): string[] => {
  if (!token) return [];
  const payload = parseJwt(token);

  // key deny phải đúng với ClaimTypesEx.PermissionDeny bên BE
  return normalizePermissions(
    payload?.["permission.deny"] ??
      payload?.permissionDeny ??
      payload?.permission_denies
  );
};

export const getPermissions = (): string[] => {
  const { token } = tokenStore.get();
  return getPermissionsFromToken(token);
};

export const getEffectivePermissionsFromToken = (
  token?: string | null
): string[] => {
  const grants = new Set(getPermissionsFromToken(token));
  const denies = getPermissionDeniesFromToken(token);

  for (const d of denies) grants.delete(d);

  return Array.from(grants);
};

export const getEffectivePermissions = (): string[] => {
  const { token } = tokenStore.get();
  return getEffectivePermissionsFromToken(token);
};

export const can = (
  permission: string,
  permissions = getEffectivePermissions()
) => permissions.includes(permission);

export const canAny = (
  required: string[] = [],
  permissions = getEffectivePermissions()
) => required.length === 0 || required.some((p) => permissions.includes(p));

export const canAll = (
  required: string[] = [],
  permissions = getEffectivePermissions()
) => required.length === 0 || required.every((p) => permissions.includes(p));
